import React, { useEffect, useState } from 'react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import {
  DispatchDetailBatch,
  DispatchDetailStop,
  stopFitsTrip,
} from '@/services/api/dispatches';

import { StopKindBadge, TripKindBadge } from '../components/DispatchBadges';

interface MoveStopModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The stop being moved. Null = closed. */
  stop: DispatchDetailStop | null;
  /** Batch the stop currently belongs to — shown disabled in the list. */
  sourceBatchId: string | null;
  /** Rider that owns the stop — targets are scoped to this rider's trips. */
  riderName?: string | null;
  /** The rider's own PLANNED trips (candidates + the source). */
  candidateBatches: DispatchDetailBatch[];
  capWeightKg: number;
  capVolumeM3: number;
  onSubmit: (toBatch: DispatchDetailBatch) => Promise<void>;
}

const fmtWeight = (kg: number) => `${kg.toFixed(2)} kg`;
const fmtVolume = (m3: number) => `${m3.toFixed(3)} m³`;

/** Projected-load meter for one dimension after the stop lands here. */
const ProjBar: React.FC<{
  label: string;
  projected: number;
  cap: number;
  format: (n: number) => string;
}> = ({ label, projected, cap, format }) => {
  const pct = cap > 0 ? projected / cap : 0;
  const over = pct > 1;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between text-[10px] tabular-nums">
        <span className="text-text-sub-600">{label}</span>
        <span
          className={
            over ? 'font-bold text-red-600' : 'font-semibold text-text-sub-600'
          }
        >
          {Math.round(pct * 100)}%
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-soft-200">
        <div
          className={`h-full ${over ? 'bg-red-500' : 'bg-accent/70'}`}
          style={{ width: `${Math.min(100, pct * 100)}%` }}
        />
      </div>
      <span className="text-[10px] text-text-soft-400 tabular-nums">
        {format(projected)} / {format(cap)}
      </span>
    </div>
  );
};

/**
 * Move a single stop into another PLANNED batch (trip) — reuses
 * `PATCH /:id { stopReassignments: [{ stopId, toBatchId, rider }] }`. The list
 * previews each candidate's PROJECTED load once this stop lands, flagging any
 * that would go over 100% (the BE doesn't block overloads — §capacity note).
 */
const MoveStopModal: React.FC<MoveStopModalProps> = ({
  isOpen,
  onClose,
  stop,
  sourceBatchId,
  riderName,
  candidateBatches,
  capWeightKg,
  capVolumeM3,
  onSubmit,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSelectedId(null);
  }, [isOpen]);

  const targets = candidateBatches.filter((b) => b.id !== sourceBatchId);
  // A pickup stop only joins a pickup run, a dropoff only a drop run — the
  // two start/end at opposite ends of the hub/pitstop. Incompatible trips stay
  // listed but locked so ops sees why. The BE enforces the same rule.
  const isCompatible = (b: DispatchDetailBatch): boolean =>
    stop == null || stopFitsTrip(stop.kind, b.stops);
  const compatibleCount = targets.filter(isCompatible).length;
  const selected = targets.find((b) => b.id === selectedId);
  const canSubmit =
    !submitting && stop != null && selected != null && isCompatible(selected);

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await onSubmit(selected);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-drag-move-2-line" />}
      iconTone="primary"
      title="Pindahkan stop ke trip lain"
      subtitle={
        stop
          ? `${stop.receiverName || stop.destinationAddress} · ${
              stop.itemCount
            } item · ${fmtWeight(stop.totalWeightKg)}`
          : undefined
      }
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
            Batal
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Memindahkan…' : 'Pindahkan'}
          </Button>
        </div>
      }
    >
      <p className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
        Pindah ke trip {riderName ? `· ${riderName}` : ''}
        <StopKindBadge kind={stop?.kind} />
      </p>
      {targets.length === 0 ? (
        <div className="rounded-lg border border-stroke-soft-200 px-3 py-4 text-center text-xs text-text-sub-600">
          {riderName ?? 'Rider ini'} cuma punya satu trip PLANNED — tidak ada
          trip lain untuk menampung stop ini.
        </div>
      ) : (
        <div className="max-h-72 space-y-2 overflow-y-auto">
          {compatibleCount === 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-800">
              Tidak ada trip sejenis — stop{' '}
              {stop?.kind === 'PICKUP' ? 'pickup' : 'dropoff'} hanya bisa masuk
              trip {stop?.kind === 'PICKUP' ? 'pickup' : 'dropoff'}.
            </div>
          )}
          {targets.map((b) => {
            const checked = selectedId === b.id;
            const compatible = isCompatible(b);
            const projWeight = b.totalWeightKg + (stop?.totalWeightKg ?? 0);
            const projVolume = b.totalVolumeM3 + (stop?.totalVolumeM3 ?? 0);
            const over =
              (capWeightKg > 0 && projWeight > capWeightKg) ||
              (capVolumeM3 > 0 && projVolume > capVolumeM3);
            return (
              <label
                key={b.id}
                className={`block rounded-lg border px-3 py-2.5 transition-colors ${
                  !compatible
                    ? 'cursor-not-allowed border-stroke-soft-200 opacity-60'
                    : checked
                      ? 'cursor-pointer border-accent bg-bg-weak-50'
                      : 'cursor-pointer border-stroke-soft-200 hover:bg-bg-weak-50'
                }`}
                title={
                  compatible
                    ? undefined
                    : 'Beda jenis trip — stop dropoff dan pickup tidak bisa satu trip'
                }
              >
                <div className="flex items-center gap-2.5">
                  <input
                    type="radio"
                    name="move-stop-target"
                    checked={checked}
                    onChange={() => setSelectedId(b.id)}
                    disabled={submitting || !compatible}
                    className="h-4 w-4 border-stroke-soft-200 text-accent focus:ring-accent"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-semibold tracking-tight text-text-strong-950">
                        {b.tripSequence != null
                          ? `Trip #${b.tripSequence}`
                          : b.code}
                      </span>
                      <TripKindBadge stops={b.stops} />
                      {!compatible && (
                        <Badge status="warning" size="sm">
                          beda jenis trip
                        </Badge>
                      )}
                      {over && (
                        <Badge status="error" size="sm">
                          overload
                        </Badge>
                      )}
                    </div>
                    <span className="font-mono text-[10px] text-text-soft-400 tabular-nums">
                      {b.code} · {b.stopCount} stop · {b.itemCount} item
                    </span>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-3 pl-6">
                  <ProjBar
                    label="Weight"
                    projected={projWeight}
                    cap={capWeightKg}
                    format={fmtWeight}
                  />
                  <ProjBar
                    label="Volume"
                    projected={projVolume}
                    cap={capVolumeM3}
                    format={fmtVolume}
                  />
                </div>
              </label>
            );
          })}
        </div>
      )}
      <p className="mt-3 text-[11px] leading-relaxed text-text-sub-600">
        Stop pindah ke trip terpilih dan rutenya dihitung ulang otomatis. Tidak
        ada batas kapasitas — trip yang jadi <span className="font-semibold">
        overload
        </span>{' '}
        tetap diterima, hanya ditandai di sini.
      </p>
    </FormModal>
  );
};

export default MoveStopModal;
